import { useEffect, useRef, useState } from "react";
import { Icon, OwlMark, cn } from "@wiseowl/design-system";
import { api } from "../lib/api.js";
import { isApiError, type CompanionMessage } from "@wiseowl/shared";

interface CompanionViewProps {
  onExit: () => void;
}

interface Msg {
  from: "owl" | "me";
  text: string;
  type?: "sensing";
}

const GREETING: Msg = { from: "owl", text: "I'm here with you. What's been sitting with you today?" };

/**
 * CompanionView — dark teal-ink chat overlay, wired to /api/companion.
 *
 * Owl on the left with a mustard halo; user on the right in mustard. On
 * mount we load the most recent thread (if any); sending POSTs the
 * message and appends the owl's reply. A "sensing" indicator shows while
 * the reply is in flight. The owl rests when the daily cap is reached.
 */
export const CompanionView = ({ onExit }: CompanionViewProps) => {
  const [messages, setMessages] = useState<Msg[]>([GREETING]);
  const [draft, setDraft] = useState("");
  const [threadId, setThreadId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [resting, setResting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Resume the most recent conversation on mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const list = await api<{ threads: { id: string }[] }>("/companion/threads");
      if (cancelled || isApiError(list) || !list.data.threads[0]) return;
      const id = list.data.threads[0].id;
      const full = await api<{ messages: CompanionMessage[] }>(`/companion/threads/${id}`);
      if (cancelled || isApiError(full) || full.data.messages.length === 0) return;
      setThreadId(id);
      setMessages(full.data.messages.map((m) => ({ from: m.role, text: m.text })));
    })();
    return () => { cancelled = true; };
  }, []);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || resting) return;
    setDraft("");
    setSending(true);
    setMessages((m) => [...m, { from: "me", text }, { from: "owl", type: "sensing", text: "reflecting with you…" }]);

    const r = await api<{ threadId: string; reply: CompanionMessage }>("/companion/messages", {
      method: "POST",
      body: JSON.stringify({ threadId, text }),
    });
    setSending(false);
    setMessages((m) => m.filter((x) => x.type !== "sensing"));

    if (isApiError(r)) {
      if (r.error.code === "daily_cap") {
        setResting(true);
        setMessages((m) => [...m, { from: "owl", text: r.error.message }]);
      } else {
        setMessages((m) => [...m, { from: "owl", text: "The owl is taking a slow breath. Try once more in a moment." }]);
      }
      return;
    }
    setThreadId(r.data.threadId);
    setMessages((m) => [...m, { from: "owl", text: r.data.reply.text }]);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Companion mode"
      className="fixed inset-0 z-40 flex flex-col bg-teal-deep text-cream"
    >
      {/* Header */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-cream/[0.08] bg-ink/85 p-4 backdrop-blur">
        <button
          type="button"
          onClick={onExit}
          aria-label="Exit Companion mode"
          className="p-1 text-cream/90"
        >
          <Icon name="close" size={22} />
        </button>
        <OwlMark size={38} bgClassName="bg-mustard" glow />
        <div className="flex-1">
          <div className="font-serif text-body font-medium">Your companion</div>
          <div className="flex items-center gap-1.5 text-[11px] text-cream/60">
            <span className="h-1.5 w-1.5 rounded-pill bg-mustard" />
            here with you · private
          </div>
        </div>
        <button type="button" aria-label="History" className="p-1 text-cream/90">
          <Icon name="history" size={20} />
        </button>
      </header>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="mx-auto flex w-full max-w-[760px] flex-1 flex-col gap-3.5 overflow-y-auto px-4 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {messages.map((m, i) => (
          <Bubble key={i} m={m} />
        ))}
      </div>

      {/* Composer */}
      <div
        className="mx-auto w-full max-w-[760px] p-4"
        style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-center gap-2 rounded-[28px] border border-cream/[0.14] bg-cream/[0.08] p-1.5">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void send(); }}
            disabled={resting}
            placeholder={resting ? "The owl rests now — come back tomorrow." : "Write here…"}
            className={cn(
              "flex-1 bg-transparent px-3.5 py-3 text-body text-cream outline-none placeholder:text-cream/50 font-serif disabled:opacity-60",
              draft ? "not-italic" : "italic",
            )}
          />
          <button type="button" aria-label="Voice input" className="p-2">
            <Icon name="mic" size={20} className="text-cream/65" />
          </button>
          <button
            type="button"
            onClick={() => void send()}
            aria-label="Send"
            disabled={sending || resting || !draft.trim()}
            className="grid h-10 w-10 place-items-center rounded-pill bg-mustard text-ink disabled:opacity-50"
          >
            <Icon name="send" size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

const Bubble = ({ m }: { m: Msg }) => {
  if (m.type === "sensing") {
    return (
      <div className="ml-1 flex items-center gap-2 text-cream/55">
        <OwlMark size={22} bgClassName="bg-mustard" />
        <span className="font-mono text-[10px] uppercase tracking-wider">
          Companion · {m.text}
        </span>
        <span className="ml-1 flex gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-1 w-1 rounded-pill bg-mustard opacity-60"
              style={{ animation: `wo-blink 1.2s ${i * 0.2}s infinite` }}
            />
          ))}
        </span>
        <style>{`@keyframes wo-blink { 50% { opacity: 0; } }`}</style>
      </div>
    );
  }
  if (m.from === "owl") {
    return (
      <div className="flex max-w-[90%] items-end gap-2.5">
        <OwlMark size={28} bgClassName="bg-mustard" />
        <div
          className="rounded-[20px] rounded-bl-[4px] border border-cream/[0.08] bg-cream/[0.08] px-4 py-3 font-serif text-[15.5px] leading-snug text-cream text-pretty"
        >
          {m.text}
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-[85%] self-end">
      <div className="rounded-[20px] rounded-br-[4px] bg-mustard px-4 py-3 text-[14.5px] font-medium leading-snug text-ink">
        {m.text}
      </div>
    </div>
  );
};
